/**
 * ====================================================================
 * FIKO GD&T (Geometric Dimensioning & Tolerancing) MODULE
 * ====================================================================
 *
 * ASME Y14.5-2018 ve ISO 1101:2017 uyumlu profesyonel GD&T sistemi.
 *
 * Kullanım:
 * ```typescript
 * import {
 *   GDTManager,
 *   getGDTManager,
 *   FeatureControlFrame,
 *   DatumFeature,
 *   GDTCharacteristicType,
 *   MaterialCondition
 * } from './fiko-gdt';
 *
 * // Manager al
 * const gdtManager = getGDTManager(sahne);
 *
 * // Datum ekle
 * gdtManager.addDatum(position, 'A', { surfaceType: 'PLANE' });
 *
 * // FCF ekle
 * gdtManager.createQuickFCF(
 *   position,
 *   GDTCharacteristicType.POSITION,
 *   0.25,
 *   {
 *     zoneModifier: ToleranceZoneModifier.DIAMETER,
 *     materialCondition: MaterialCondition.MMC,
 *     datums: ['A', 'B', 'C']
 *   }
 * );
 * ```
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 * @standard ASME Y14.5-2018, ISO 1101:2017
 */

// ====================================================================
// TYPE DEFINITIONS - Enums and Constants
// ====================================================================

export {
    // Characteristic Types (enum)
    GDTCharacteristicType,

    // Symbols (const objects)
    GDT_SYMBOLS,
    GDT_NAMES_TR,
    GDT_NAMES_EN,

    // Material Conditions (enum)
    MaterialCondition,
    MATERIAL_CONDITION_SYMBOLS,

    // Tolerance Zone (enum)
    ToleranceZoneModifier,
    TOLERANCE_ZONE_SYMBOLS,

    // Datum (enum)
    DatumType,

    // Dimensions (enum)
    DimensionType,

    // Surface Texture (enums)
    SurfaceTextureParameter,
    MachiningProcess,

    // PMI (enum)
    PMIElementType,

    // Helper Functions
    requiresDatum,
    getCharacteristicCategory,
    isDeprecated,
    formatToleranceValue,
    generateFCFText
} from './GDTTypes';

// ====================================================================
// TYPE DEFINITIONS - Interfaces (export type)
// ====================================================================

export type {
    // Datum interfaces
    DatumReference,
    DatumFeatureInfo,

    // Feature Control Frame interfaces
    FCFComponent,
    FeatureControlFrameConfig,
    FCFStyleConfig,

    // Composite Tolerance
    CompositeToleranceConfig,

    // Dimensions
    DimensionConfig,

    // Surface Texture
    SurfaceTextureConfig,

    // PMI
    PMISavedView,
    PMIData,
    PMITable,

    // Validation
    GDTValidationResult,
    GDTValidationError,
    GDTValidationWarning
} from './GDTTypes';

// ====================================================================
// SYMBOL DRAWING
// ====================================================================

export {
    // Main symbol drawing
    drawGDTSymbol,
    drawMaterialCondition,
    drawToleranceZoneModifier,

    // Datum symbols
    drawDatumFeature,
    drawDatumTarget,

    // FCF drawing
    drawFCFCell,
    drawFCFSymbolCell,
    calculateFCFWidth,

    // Utilities
    createSymbolCanvas,
    createAllSymbolsPreview
} from './GDTSymbols';

export type { SymbolDrawConfig } from './GDTSymbols';

// ====================================================================
// 3D COMPONENTS
// ====================================================================

export {
    // Feature Control Frame
    FeatureControlFrame,
    createFCF
} from './FeatureControlFrame';

export {
    // Datum Feature
    DatumFeature,
    DatumTarget,
    createDatum,
    createDatumTarget
} from './DatumFeature';

export type { DatumStyleConfig } from './DatumFeature';

// ====================================================================
// MANAGER
// ====================================================================

export {
    // Manager
    GDTManager,
    getGDTManager
} from './GDTManager';

export type { GDTEventType, GDTEvent } from './GDTManager';

// ====================================================================
// VERSION & INFO
// ====================================================================

export const GDT_VERSION = '2.0.0';
export const GDT_STANDARD = 'ASME Y14.5-2018 / ISO 1101:2017';

/**
 * GD&T modül bilgisi
 */
export const GDT_INFO = {
    version: GDT_VERSION,
    standard: GDT_STANDARD,
    features: [
        'Full ASME Y14.5-2018 symbol support',
        'Feature Control Frame (FCF) rendering',
        'Datum Feature & Target support',
        'Material Condition Modifiers',
        'Tolerance Zone Modifiers',
        'PMI (Product Manufacturing Information)',
        'Saved Views',
        'Auto-validation',
        'Fixed-size annotation (zoom-independent)',
        'Leader lines with arrows',
        'Serialization/Deserialization',
        'Event system'
    ],
    deprecatedCharacteristics: [
        'CONCENTRICITY - Use POSITION instead',
        'SYMMETRY - Use POSITION instead'
    ]
};
